import * as Sentry from '@sentry/nextjs';

import { getFormDiagnostics } from './src/components/react-hook-form/formDiagnostics';
import {
  getAppVersion,
  getEnvironment,
  isDevelopmentMode,
} from './src/utils/environmentUtils';

const blockedErrorMessages = [
  // this error shows up frequently and has no user-facing impact
  'ResizeObserver loop completed with undelivered notifications',
  // CefSharp security scanners can reject a promise while they inspect links.
  'Object Not Found Matching Id:',
  // generated during tests and from internal dev tools, no user-facicng impact
  'BootstrapWaterfall',

  // These are both from subscriptions and just get triggered sometimes
  // on unmount
  'WorkflowTrackerUpdates',
  'NotificationAppCreated',
];

const blockedInvalidSubdomainHost =
  /^(?:_domainkey|_bimi|_mta-sts|_dnsauth|as3_[^.]+)\.withluminary\.com$/i;

function matchesBlockedInvalidSubdomainEvent(
  event: Sentry.ErrorEvent,
  exception: Sentry.Exception
): boolean {
  if (exception.value !== 'Invalid subdomain' || !event.request?.url) {
    return false;
  }

  try {
    return blockedInvalidSubdomainHost.test(
      new URL(event.request.url).hostname
    );
  } catch {
    return false;
  }
}

/**
 * @description Returns true if the event should be blocked from being sent to Sentry
 */
function matchesBlockedEvent(event: Sentry.ErrorEvent): boolean {
  // to be extra safe, only block events with a single exception
  if (event.exception?.values?.length !== 1) return false;

  const exception = event.exception.values[0];
  if (!exception) return false;

  const hasBlockedMessage = blockedErrorMessages.some((blockedMessage) =>
    (exception.value ?? '').includes(blockedMessage)
  );

  if (hasBlockedMessage) {
    return true;
  }

  return matchesBlockedInvalidSubdomainEvent(event, exception);
}

function beforeSentrySend(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  if (matchesBlockedEvent(event)) return null;
  if (isDevelopmentMode()) return null;

  // React exposes this failure as a message in development and code 185 in production.
  const isUpdateDepthError = event.exception?.values?.some(
    ({ value }) =>
      value?.startsWith('Maximum update depth exceeded') ||
      value?.startsWith('Minified React error #185;')
  );
  if (isUpdateDepthError) {
    const formDiagnostics = getFormDiagnostics();
    if (formDiagnostics) {
      event.contexts = {
        ...event.contexts,
        form_update_depth: formDiagnostics,
      };
    }
  }

  return event;
}

// Shared Sentry config
export const sentryConfig:
  | Sentry.BrowserOptions
  | Sentry.NodeOptions
  | Sentry.EdgeOptions = {
  dsn: 'https://b072d3c5888147c8b64b239c7a6cfc5a@o4503902548262912.ingest.sentry.io/4503959315283968',

  // Setting this option to true will print useful information to the console while you're setting up Sentry.
  debug: false,

  beforeSend: beforeSentrySend,

  environment: getEnvironment(),
  release: getAppVersion(),
};
